/**
 * 개념 추이 모달 — 선택한 매스컨셉의 '직접 개념 숙련도(r)' 시계열(X=시험일, Y=r) 라인차트.
 *
 * 데이터는 일별 재생과 동일한 프레임(envelope)에서 온다. 부모(AITrackerContent)가
 * ensureFramesLoaded(shouldAbort, onProgress)로 프레임 프리로드를 수행하고 envelope 를 돌려준다.
 * 이 모달은 재생 상태(playbackGenRef 등)를 건드리지 않고 자체 취소 토큰(cancelledRef)으로 abort 한다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import MathName from './SyllabusV2/MathName';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { Modal } from './common';
import { ACCENT, DIFFICULTY_SOLID, SPARK, TRACK, INK_FADED, CONCEPT_TYPE_COLOR } from './common/report/reportColors';
import { buildConceptSeries, topMoversByDelta } from '../utils/aitrackerTrendSeries';
import TimepointProblemModal from './TimepointProblemModal';

const AUTO_TOP_N = 12; // 선택이 없을 때 자동 표시할 '변화량 큰' 개념 수

// 라인 시리즈 색 — reportColors solid 토큰에서 인접색을 피해 구성(임의 팔레트 발명 회피). 초과 시 순환.
const LINE_COLORS = [
  ACCENT,               // #4ABC85 green
  DIFFICULTY_SOLID[3],  // #2563EB blue
  DIFFICULTY_SOLID[5],  // #DC2626 red
  DIFFICULTY_SOLID[4],  // #EA580C orange
  SPARK.mid,            // #A16207 amber-700
  DIFFICULTY_SOLID[1],  // #EAB308 yellow
];
const AXIS = INK_FADED; // 축·기준선 중립 색(토큰: 40% ink) — 시리즈 팔레트 아님
const GRID = TRACK;     // 그리드/축선 (reportColors.TRACK = gray200)

function EmptyState({ children }) {
  return (
    <div className="flex h-[360px] items-center justify-center px-6 text-center text-sm text-gray-500">
      {children}
    </div>
  );
}

// 매스컨셉 배지(순번 + 개념타입 + 이름) — 프로젝트 공통 배지 스타일(ProblemPreviewPanel과 동일).
// concept = { order, concept_type }, nameHtml = LaTeX 렌더된 개념명(HTML). dim/focused는 범례 강조 상태.
function ConceptBadgeLabel({ concept, nameHtml, dim, focused }) {
  return (
    <span
      className="inline-flex items-center gap-1 align-middle"
      style={{ opacity: dim ? 0.35 : 1, fontWeight: focused ? 700 : 400 }}
    >
      {concept && concept.order != null && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-700"
          style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}
        >
          {concept.order}
        </span>
      )}
      {concept && concept.concept_type && (
        <span
          className="inline-flex items-center justify-center font-semibold text-gray-800"
          style={{ backgroundColor: CONCEPT_TYPE_COLOR[concept.concept_type] || CONCEPT_TYPE_COLOR._, borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '8px' }}
        >
          {concept.concept_type}
        </span>
      )}
      <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: nameHtml }} />
    </span>
  );
}

function ConceptTrendModal({ isOpen, onClose, cids = [], ensureFramesLoaded, getConceptShortName, getConceptDisplayName, studentId, useV2Preview = false, includeV2Assignments = false }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error' (오픈 전 기본=loading으로 빈상태 깜빡임 방지)
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [envelope, setEnvelope] = useState(null);
  const [focusedCid, setFocusedCid] = useState(null); // 범례 클릭으로 강조된 개념(null=전체 동일)
  const [problemPoint, setProblemPoint] = useState(null); // 시점 클릭 → {date, cid} (그 시점 시험 문제 모달)
  const cancelledRef = useRef(false);

  // 모달이 열릴 때 프레임 프리로드(선택 유무 무관 — 선택이 없으면 자동 상위개념 산정에 프레임 필요).
  // 버튼이 날짜<2에서 비활성이라 여기선 프레임이 항상 유의미. 닫힘/언마운트 시 in-flight 폐기.
  useEffect(() => {
    if (!isOpen) return undefined;
    cancelledRef.current = false;
    setEnvelope(null);
    setFocusedCid(null);
    setProblemPoint(null); // 재오픈 시 stale 시점모달 방지
    setStatus('loading');
    setProgress({ loaded: 0, total: 0 });
    ensureFramesLoaded(
      () => cancelledRef.current,
      (loaded, total) => { if (!cancelledRef.current) setProgress({ loaded, total }); },
    ).then((env) => {
      if (cancelledRef.current) return;
      if (!env) { setStatus('error'); return; }
      setEnvelope(env);
      setStatus('ready');
    }).catch(() => {
      // ensureFramesLoaded는 실패 시 null 반환이 계약이나, 예외 방어(스피너 영구고정·unhandled rejection 차단)
      if (!cancelledRef.current) setStatus('error');
    });
    return () => { cancelledRef.current = true; };
  }, [isOpen, ensureFramesLoaded]);

  // 선택 개념이 있으면 그 개념, 없으면 변화량(|Δr|) 큰 상위 N개 자동.
  const isAuto = cids.length === 0;
  const autoCids = useMemo(
    () => (isAuto ? topMoversByDelta(envelope, AUTO_TOP_N, 'r') : []),
    [isAuto, envelope],
  );
  const effectiveCids = isAuto ? autoCids : cids;
  const { rows, keys } = useMemo(
    () => buildConceptSeries(envelope, effectiveCids, 'r'),
    [envelope, effectiveCids],
  );

  // 범례 클릭 강조: focusedCid 가 현재 라인(keys) 에 있을 때만 유효(개념 목록 변하면 자동 해제).
  const activeFocus = focusedCid != null && keys.includes(focusedCid) ? focusedCid : null;

  let body;
  if (status === 'loading') {
    body = (
      <div className="flex h-[360px] flex-col items-center justify-center gap-3 text-sm text-gray-500">
        <span className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <span>시점별 데이터 불러오는 중… {progress.total ? `${progress.loaded}/${progress.total}` : ''}</span>
      </div>
    );
  } else if (status === 'error') {
    body = <EmptyState>추이 데이터를 불러오지 못했습니다. 잠시 후 다시 시도하세요.</EmptyState>;
  } else if (keys.length === 0 || rows.length === 0) {
    body = <EmptyState>표시할 추이 데이터가 없습니다.</EmptyState>;
  } else {
    body = (
      <div className="flex flex-col">
        <div className="mb-2 flex flex-wrap items-center gap-2 px-1 text-xs text-gray-500">
          <span>세로축 <span className="font-semibold">r</span> = 직접 개념 숙련도(양수=성장) · 가로축 = 시험일 · 시점(세로 영역) 클릭 = 그 시점 문제(개념은 범례로 강조)</span>
          {isAuto && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
              선택 없음 · 변화량 큰 상위 {keys.length}개 자동
            </span>
          )}
        </div>
        {/* overflow-hidden: 우측 끝 호버 시 recharts 툴팁이 측정 전 1프레임 동안 컨테이너 밖으로 튀어
            상위 스크롤 컨테이너(모달 바디 overflow-y-auto→x=auto 계산)에 가로 스크롤바가 깜빡이는 것 차단.
            정상 상태 툴팁은 차트 안으로 되돌아오므로 잘리지 않음. */}
        <div className="h-[420px] max-h-[60vh] w-full overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={rows}
              margin={{ top: 8, right: 16, left: -8, bottom: 0 }}
              onClick={(state) => {
                // 플롯 어디든 클릭 → 그 시점(날짜) 획득.
                // 개념 드릴다운 대상: 범례 강조(activeFocus), 또는 사용자가 개념을 선택해 연 단일 라인(!isAuto && keys.length===1)일 때만 그 개념(=필터 진입).
                // 자동모드(미선택)·다중 라인이면 cid=null → 그 시점 '전체 문제'로 진입(요약줄 '매스컨셉 미선택' 표시).
                const d = state && state.activeLabel;
                if (!d) return;
                const cid = activeFocus != null ? activeFocus : (!isAuto && keys.length === 1 ? keys[0] : null);
                setProblemPoint({ date: d, cid });
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: AXIS }} tickLine={false} axisLine={{ stroke: GRID }} />
              <YAxis
                tick={{ fontSize: 10, fill: AXIS }}
                tickLine={false}
                axisLine={false}
                domain={[(min) => Math.min(0, min), (max) => Math.max(0, max)]}
                tickFormatter={(v) => v.toFixed(1)}
              />
              <ReferenceLine y={0} stroke={AXIS} strokeDasharray="2 2" />
              <Tooltip
                contentStyle={{ fontSize: 11, borderRadius: 8 }}
                formatter={(v, name) => [v == null ? '-' : v.toFixed(3), <MathName text={name} compact />]}   /* 선 이름의 $…$ 도 수식으로(범례와 같게) */
              />
              <Legend
                wrapperStyle={{ fontSize: 11, cursor: 'pointer' }}
                onClick={(o) => {
                  const cid = o?.dataKey ?? o?.payload?.dataKey ?? o?.value;
                  if (cid != null) setFocusedCid((c) => (String(c) === String(cid) ? null : String(cid)));
                }}
                formatter={(value, entry) => {
                  const cid = entry?.dataKey;
                  const focused = activeFocus != null && String(activeFocus) === String(cid);
                  const dim = activeFocus != null && !focused;
                  const info = getConceptDisplayName ? getConceptDisplayName(cid) : null;
                  return (
                    <ConceptBadgeLabel
                      concept={info?.concept}
                      nameHtml={info?.rendered || value}
                      dim={dim}
                      focused={focused}
                    />
                  );
                }}
              />
              {keys.map((cid, i) => {
                const focused = activeFocus === cid;
                const dim = activeFocus != null && !focused;
                let strokeWidth = 2;
                if (focused) strokeWidth = 3.5;
                else if (dim) strokeWidth = 1.5;
                return (
                  <Line
                    key={cid}
                    type="monotone"
                    dataKey={cid}
                    name={getConceptShortName ? getConceptShortName(cid) : String(cid)}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    strokeWidth={strokeWidth}
                    strokeOpacity={dim ? 0.18 : 1}
                    dot={dim ? false : { r: 2 }}
                    activeDot={{ r: focused ? 6 : 4, style: { cursor: 'pointer' } }}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="개념 추이 · 직접 개념(r)" size="max" zIndex={9999940}>
      {body}
      {/* 시점 클릭 → 그 시점 시험 문제(정오·개념 강조). 상위 z로 이 모달 위에 겹침. */}
      <TimepointProblemModal
        isOpen={!!problemPoint}
        onClose={() => setProblemPoint(null)}
        studentId={studentId}
        date={problemPoint?.date}
        conceptCid={problemPoint?.cid ?? null}
        conceptName={problemPoint?.cid != null ? (getConceptShortName ? getConceptShortName(problemPoint.cid) : String(problemPoint.cid)) : ''}
        useV2Preview={useV2Preview}
        includeV2Assignments={includeV2Assignments}
      />
    </Modal>
  );
}

export default ConceptTrendModal;
